import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useEntitlement } from "@/hooks/useEntitlement";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Lightbulb, ThumbsUp, ThumbsDown, Check, X, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

interface PeerExplanation {
  id: string;
  body: string;
  status: "pending" | "approved" | "rejected";
  user_id: string;
  created_at: string;
  author_name?: string;
  upvotes: number;
  downvotes: number;
  user_vote?: number;
}

export function PeerExplanations({ mcqId }: { mcqId: string }) {
  const { userId, isAdmin, hasActiveAccess } = useEntitlement();
  const [explanations, setExplanations] = useState<PeerExplanation[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canParticipate = isAdmin || hasActiveAccess;

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    const { data: rows, error } = await supabase
      .from("peer_explanations")
      .select("id, body, status, user_id, created_at")
      .eq("mcq_id", mcqId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      setLoading(false);
      return;
    }

    const ids = (rows ?? []).map((r) => r.id);
    const userIds = Array.from(new Set((rows ?? []).map((r) => r.user_id)));

    const [votesRes, profilesRes, myVotesRes] = await Promise.all([
      ids.length
        ? supabase.from("peer_explanation_votes").select("explanation_id, value").in("explanation_id", ids)
        : Promise.resolve({ data: [] as any[], error: null }),
      userIds.length
        ? supabase.from("profiles").select("id, full_name").in("id", userIds)
        : Promise.resolve({ data: [] as any[], error: null }),
      ids.length
        ? supabase
            .from("peer_explanation_votes")
            .select("explanation_id, value")
            .eq("user_id", userId)
            .in("explanation_id", ids)
        : Promise.resolve({ data: [] as any[], error: null }),
    ]);

    const voteMap = new Map<string, { up: number; down: number }>();
    (votesRes.data ?? []).forEach((v: any) => {
      const cur = voteMap.get(v.explanation_id) ?? { up: 0, down: 0 };
      if (v.value > 0) cur.up += 1;
      else if (v.value < 0) cur.down += 1;
      voteMap.set(v.explanation_id, cur);
    });

    const profileMap = new Map<string, string>();
    (profilesRes.data ?? []).forEach((p: any) => profileMap.set(p.id, p.full_name ?? "Anonymous"));

    const myVoteMap = new Map<string, number>();
    (myVotesRes.data ?? []).forEach((v: any) => myVoteMap.set(v.explanation_id, v.value));

    setExplanations(
      (rows ?? []).map((r) => ({
        ...(r as any),
        author_name: profileMap.get(r.user_id) ?? "Anonymous",
        upvotes: voteMap.get(r.id)?.up ?? 0,
        downvotes: voteMap.get(r.id)?.down ?? 0,
        user_vote: myVoteMap.get(r.id),
      }))
    );
    setLoading(false);
  };

  useEffect(() => {
    if (userId) load();
  }, [mcqId, userId]);

  const submit = async () => {
    if (!userId || !body.trim()) return;
    setSubmitting(true);
    const { error } = await supabase.from("peer_explanations").insert({
      mcq_id: mcqId,
      user_id: userId,
      body: body.trim(),
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setBody("");
    toast.success("Submitted for review");
    load();
  };

  const vote = async (explanationId: string, value: number) => {
    if (!userId) return;
    const existing = explanations.find((e) => e.id === explanationId)?.user_vote;
    if (existing === value) {
      await supabase
        .from("peer_explanation_votes")
        .delete()
        .eq("explanation_id", explanationId)
        .eq("user_id", userId);
    } else {
      await supabase
        .from("peer_explanation_votes")
        .upsert({ explanation_id: explanationId, user_id: userId, value }, { onConflict: "explanation_id,user_id" });
    }
    load();
  };

  const moderate = async (id: string, status: "approved" | "rejected") => {
    const { error } = await supabase
      .from("peer_explanations")
      .update({ status, reviewed_by: userId, reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    toast.success(`Explanation ${status}`);
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("peer_explanations").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  };

  if (!canParticipate) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Lightbulb className="h-4 w-4" />
          Subscribe to view and submit peer explanations.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">Peer Explanations</h3>
        <Badge variant="outline" className="ml-auto">
          {explanations.filter((e) => e.status === "approved").length} approved
        </Badge>
      </div>

      <div className="space-y-2">
        <Textarea
          placeholder="Share an alternative explanation… (admin will review before publishing)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={submit} disabled={submitting || !body.trim()}>
            {submitting && <Loader2 className="h-3 w-3 mr-2 animate-spin" />}
            Submit for review
          </Button>
        </div>
      </div>

      <Separator />

      {loading ? (
        <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : explanations.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No peer explanations yet. Be the first to contribute!
        </p>
      ) : (
        <div className="space-y-3">
          {explanations
            .filter((e) => e.status === "approved" || e.user_id === userId || isAdmin)
            .map((e) => (
              <div key={e.id} className="p-3 rounded-lg border bg-muted/30 space-y-2">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{e.author_name}</span>
                  <span>·</span>
                  <span>{formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}</span>
                  {e.status === "pending" && (
                    <Badge variant="secondary" className="ml-auto gap-1">
                      <Clock className="h-3 w-3" /> Pending review
                    </Badge>
                  )}
                  {e.status === "rejected" && (
                    <Badge variant="destructive" className="ml-auto">Rejected</Badge>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap">{e.body}</p>

                <div className="flex items-center gap-1">
                  {e.status === "approved" && (
                    <>
                      <Button
                        variant={e.user_vote === 1 ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => vote(e.id, 1)}
                      >
                        <ThumbsUp className="h-3 w-3 mr-1" />
                        {e.upvotes}
                      </Button>
                      <Button
                        variant={e.user_vote === -1 ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => vote(e.id, -1)}
                      >
                        <ThumbsDown className="h-3 w-3 mr-1" />
                        {e.downvotes}
                      </Button>
                    </>
                  )}

                  {isAdmin && e.status !== "approved" && (
                    <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={() => moderate(e.id, "approved")}>
                      <Check className="h-3 w-3 mr-1" /> Approve
                    </Button>
                  )}
                  {isAdmin && e.status !== "rejected" && (
                    <Button size="sm" variant="outline" className="h-7" onClick={() => moderate(e.id, "rejected")}>
                      <X className="h-3 w-3 mr-1" /> Reject
                    </Button>
                  )}
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => remove(e.id)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </Card>
  );
}
